<template>
  <div class="h-full flex flex-col p-6">
    <!-- Header Section -->
    <div class="bg-white rounded-lg shadow mb-6">
      <div class="p-4">
        <!-- Date Selection -->
        <div class="mb-4">
          <NavigationDateSelector v-model="selectedDate" @dateSelected="selectDate" />
        </div>

        <!-- Bond Type Selection -->
        <div class="flex gap-2">
          <button v-for="type in bondTypes" :key="type.id" @click="selectBondType(type.id)"
            class="px-4 py-2 rounded-md text-sm font-medium" :class="[
              selectedBondType === type.id
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            ]">
            {{ type.name }}
          </button>
          <button @click="selectBondType('UNCATEGORIZED')" class="px-4 py-2 rounded-md text-sm font-medium" :class="[
            selectedBondType === 'UNCATEGORIZED'
              ? 'bg-gray-800 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          ]">
            미분류
          </button>
        </div>
      </div>
    </div>

    <!-- Content Section -->
    <div class="flex-1 bg-white rounded-lg shadow">
      <div class="p-4 border-b border-gray-200">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-medium text-gray-900">매수 호가 목록</h2>
          <div class="flex items-center gap-4">
            <!-- Search Input -->
            <div class="w-64">
              <input v-model="searchKeyword" type="search" placeholder="검색어를 입력하세요"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @input="handleSearch" />
            </div>
            <!-- Refresh Button -->
            <button @click="refreshData" class="text-gray-600 hover:text-gray-900" :disabled="isLoading">
              <div class="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="{ 'animate-spin': isLoading }"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span class="text-sm">새로고침</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Ask List -->
      <div class="overflow-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="w-28 px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                시간
              </th>
              <th class="w-64 px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                내용
              </th>
              <th class="w-64 px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                발신자
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="ask in buyAsks" :key="ask.chatId">
              <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ formatTime(ask.chatDateTime) }}
              </td>
              <td class="px-4 py-3 text-sm font-medium text-gray-900">
                {{ ask.content }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ ask.senderName }} {{ ask.senderAddress }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { getTodayStr } from '@/utils/util';
import askService from '@/service/creditQuoteService';
import NavigationDateSelector from '@/components/NavigationDateSelector.vue';

export default {
  name: 'BuyView',
  components: {
    NavigationDateSelector,
  },
  setup() {
    const selectedDate = ref(getTodayStr());
    const selectedBondType = ref('PUBLIC');
    const buyAsks = ref([]);
    const isLoading = ref(false);
    const chatAnalysisTime = ref(null);
    const searchKeyword = ref('');

    const bondTypes = [
      { id: 'PUBLIC', name: '공사채' },
      { id: 'BANK', name: '은행채' },
      { id: 'CREDIT_FINANCE', name: '여전채' },
      { id: 'COMPANY', name: '회사채' },
    ];

    const getBondTypeName = type => {
      const found = bondTypes.find(t => t.id === type);
      return found ? found.name : '미분류';
    };

    const formatTime = dateTime => {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return `${String(date.getHours()).padStart(2, '0')}:${String(
        date.getMinutes()
      ).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
    };

    const formatDateDisplay = dateStr => {
      const date = new Date(dateStr);
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();

      const month = date.getMonth() + 1;
      const day = date.getDate();
      const dayOfWeek = new Intl.DateTimeFormat('ko-KR', {
        weekday: 'short',
      }).format(date);

      return `${month}월 ${day}일 (${dayOfWeek})${isToday ? ' 오늘' : ''}`;
    };

    const fetchData = async () => {
      isLoading.value = true;
      try {
        const asksResult = await askService.findBuyAsk(selectedDate.value);
        buyAsks.value = asksResult;
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const selectDate = async date => {
      selectedDate.value = date;
      await fetchData();
    };

    const selectBondType = typeId => {
      selectedBondType.value = typeId;
    };

    const refreshData = () => {
      if (!isLoading.value) {
        fetchData();
      }
    };

    const filteredBuyAsks = computed(() => {
      let filtered = buyAsks.value;

      // 채권 유형 필터링
      if (selectedBondType.value) {
        filtered = filtered.filter(ask => {
          if (selectedBondType.value === 'UNCATEGORIZED') {
            return ask.bondType === 'UNCATEGORIZED';
          }
          return ask.creditType === selectedBondType.value;
        });
      }

      // 검색어 필터링
      if (searchKeyword.value) {
        const keyword = searchKeyword.value.toLowerCase();
        filtered = filtered.filter(ask => {
          return ask.content.toLowerCase().includes(keyword);
        });
      }

      return filtered;
    });

    const handleSearch = e => {
      searchKeyword.value = e.target.value;
    };

    onMounted(() => {
      fetchData();
    });

    return {
      selectedDate,
      selectedBondType,
      buyAsks: filteredBuyAsks,
      isLoading,
      chatAnalysisTime,
      bondTypes,
      formatDateDisplay,
      formatTime,
      getBondTypeName,
      selectDate,
      selectBondType,
      refreshData,
      searchKeyword,
      handleSearch,
    };
  },
};
</script>
